import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Dialog,
  Select,
  Divider,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Typography,
  FormControl,
  FormHelperText,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import dayjs from "dayjs";
import moment from "moment";
import { Autocomplete } from "@material-ui/lab";
import { CheckBoxOutlineBlank, CheckBox } from "@material-ui/icons";
import { createStyles, makeStyles } from "@material-ui/core/styles";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import MultiFileManager from "../../File/MultiFileManager/MultiFileManager";
import RewardBonusCheckboxGroup from "../RewardBonusCheckboxGroup";
import Other from "../ScientificResearchInsertForm/Other";
import Chapter from "../ScientificResearchInsertForm/Chapter";
import Conference from "../ScientificResearchInsertForm/Conference";
import WorkingPaper from "../ScientificResearchInsertForm/WorkingPaper";
import JournalDomestic from "../ScientificResearchInsertForm/JournalDomestic";
import ConferenceDomestic from "../ScientificResearchInsertForm/ConferenceDomestic";
import JournalInternational from "../ScientificResearchInsertForm/JournalInternational";
import EmploymentContractTable from "../../../components/EmployContractModal/EmploymentContractTable";
import EmploymentContractDetailTable from "../../../components/EmployContractModal/EmploymentContractDetailTable";
import { uploadMultiFile } from "../../../reducer/file/action";
import { resetDetail } from "../../../reducer/journal/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import {
  insertScientificResearchOther,
  updateScientificResearchOther,
} from "../../../reducer/scientificResearchOther/action";
import { getAllEmploymentContractByProfileId } from "../../../reducer/employmentContract/action";
import { insertScientificResearch, updateScientificResearch } from "../../../reducer/scientificResearch/action";
import { getTimeResearchInformation } from "../../../reducer/timeResearchInformation/action";
import { notification } from "../../../reducer/home/action";

function SolutionInsertModal(props) {
  const { inserting, setInserting, getTimeResearchInformation } = props;

  const [detail, setDetail] = useState(null);
  const [values, setValues] = useState({ purposeId: 5 });
  // profileList: faculty/staff members only (internal + external). Students live in
  // studentList — separated per UX request so the two distinct kinds of contributors
  // each get their own box. The two are merged back into one array on submit so the BE
  // shape (ScientificResearchProfile[]) is unchanged.
  const [profileList, setProfileList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [helperText, setHelperText] = useState(false);
  const [timeResearchYear, setTimeResearchYear] = useState(0);
  // Two parallel lists driven by the same `time_research_details` rows. The "Loại công
  // trình" picker uses rows without bonus_code (for hours/points); the "Loại công trình
  // tính thưởng" picker uses rows with bonus_code != '' and is only shown when purpose
  // is Tính thưởng. See useEffect below.
  const [timeResearchDetailList, setTimeResearchDetailList] = useState([]);
  const [timeResearchDetailListForBonus, setTimeResearchDetailListForBonus] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const dateFormat = (value) => (value ? dayjs(value).format("YYYY-MM-DDTHH:mm:ssZ") : null);

  // Tự gán bản thân vào danh sách tác giả
  useEffect(() => {
    if (props.inserting) {
      // Reset newFiles khi mở modal mới
      setNewFiles([]);
    }
    if (props.profile?.id && profileList.length === 0) {
      setProfileList([
        {
          profileId: props.profile.id,
          roleInPaper: "main",
          statusInProject: "K",
          isInternal: true,
          isStudent: false,
          externalName: "",
          orgCount: 1,
          orgRatio: 1,
        },
      ]);
    }
  }, [props.inserting, props.profile?.id]);

  // Đổi filter thì reset giá trị
  useEffect(() => {
    setDetail(null);
    setValues({ purposeId: 5 });
    setProfileList([]);
    setStudentList([]);
  }, [props.filter]);

  // Bỏ KPI bỏ luôn contract
  useEffect(() => {
    if (!values?.profileIdForKpi) handleChangeForm("contractIdForKpi", null);
  }, [values?.profileIdForKpi]);

  useEffect(() => {
    if (
      props.timeResearchInformationList.slice(0, 1)?.shift()?.year !== timeResearchYear &&
      !props.isFetchingTimeResearchInformation
    )
      getTimeResearchInformation(0, timeResearchYear);
  }, [timeResearchYear]);

  // Auto-fill the bonus picker when:
  //   (a) purpose switches to Tính thưởng and the legacy picker is already populated, or
  //   (b) the bonus list arrives (later) for an already-set legacy code.
  // We never override an existing user choice — only fill when the bonus picker is empty
  // AND the bonus list contains a row whose code matches the legacy picker.
  useEffect(() => {
    const isReward = values?.purposeId === 2 || values?.purposeId === 99999999;
    if (!isReward) return;
    if (!values?.timeResearchDetailCode) return;
    if (values?.timeResearchDetailCodeForBonus) return;
    const match = timeResearchDetailListForBonus?.find((d) => d.code === values.timeResearchDetailCode);
    if (match) setValues((prev) => ({ ...prev, timeResearchDetailCodeForBonus: match.code }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.purposeId, values?.timeResearchDetailCode, timeResearchDetailListForBonus]);
  // console.log(props.timeResearchInformationList, timeResearchDetailList);

  const handleChangeForm = (name, value) => {
    setValues({ ...values, [name]: value });
    if (name === "purposeId") {
      setValues({
        ...values,
        purposeId: value,
        profileIdForKpi: null,
        contractIdForKpi: null,
        profileIdForReward: null,
      });
      return;
    }
    // Auto-fill bonus picker when the legacy picker changes — only if the same code
    // happens to exist in the bonus-only list. Disjoint by construction in most setups,
    // so this is a no-op except when an admin set the same `code` on both sides.
    if (name === "timeResearchDetailCode" && (values?.purposeId === 2 || values?.purposeId === 99999999)) {
      const match = timeResearchDetailListForBonus?.find((d) => d.code === value);
      if (match) {
        setValues((prev) => ({ ...prev, timeResearchDetailCode: value, timeResearchDetailCodeForBonus: match.code }));
      }
    }
  };

  const handleChangeProfileList = (index, name, value) => {
    const temp = profileList;
    const tempElm = profileList[index];
    temp.splice(index, 1, { ...tempElm, [name]: value });
    setProfileList([...temp]);
  };

  const handleInsert = () => {
    const temp = profileList;
    temp.push({
      profileId: null,
      roleInPaper: "member",
      statusInProject: "K",
      isInternal: true,
      isStudent: false,
      externalName: "",
      orgCount: 1,
      orgRatio: 1,
    });
    setProfileList([...temp]);
  };

  const handleRemove = (index) => {
    if (profileList[index].profileId === values?.profileIdForKpi) handleChangeForm("profileIdForKpi", null);

    const temp = profileList.filter((e, id) => id !== index);
    setProfileList([...temp]);
  };

  // ---- Student list handlers (separate box) ----
  const handleChangeStudentList = (index, name, value) => {
    const temp = [...studentList];
    temp.splice(index, 1, { ...temp[index], [name]: value });
    setStudentList(temp);
  };
  const handleInsertStudent = () => {
    setStudentList([
      ...studentList,
      {
        // Students are persisted as ScientificResearchProfile rows with isStudent=true
        // and isInternal=false; profileId stays null.
        profileId: null,
        isInternal: false,
        isStudent: true,
        roleInPaper: "member",
        statusInProject: "K",
        externalName: "",
        studentName: "",
        studentCode: "",
        studentClass: "",
        orgCount: 1,
        orgRatio: 1,
      },
    ]);
  };
  const handleRemoveStudent = (index) => {
    setStudentList(studentList.filter((_, id) => id !== index));
  };

  const handleClose = () => {
    setInserting(false);
    props.resetDetail();
    setValues({ purposeId: 5 });
    setProfileList([]);
    setStudentList([]);
    setHelperText(false);
    setNewFiles([]);
  };

  const handleSubmit = (e) => {
    props.resetDetail();
    // Validate each list independently:
    //   profileList:  internal → profileId required; external → externalName required
    //   studentList:  studentName required (code/class are optional but recommended)
    const hasInvalidProfile = profileList.some((m) => {
      if (m.isInternal === false) return !m.externalName;
      return m.profileId === null || m.profileId === undefined;
    });
    const hasInvalidStudent = studentList.some((s) => !s.studentName);
    const hasInvalid = hasInvalidProfile || hasInvalidStudent;

    // Merge profileList + studentList into the single array the BE expects.
    // Students are flagged with isStudent=true / isInternal=false.
    const mergedList = [
      ...profileList,
      ...studentList.map((s) => ({ ...s, isInternal: false, isStudent: true, profileId: null })),
    ];

    if (!hasInvalid) {
      setInserting(false);
      const d = new Date();

      const tempListAuthor = values?.listAuthor?.split(",").map((author) => author.trimStart());

      if (props.filter !== "other") {
        if (props.filter === "conference") {
          // if (values.conferenceId) {
          // console.log("insert scientific normal");
          props
            .insertScientificResearch({
              ...values,
              purpose: undefined,
              roleInPaper: undefined,
              dashboardYear: new Date(dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD"))).getFullYear(),
              listAuthor: tempListAuthor,
              journalId: values?.journalId,
              journalDomesticId: values?.journalDomesticId,
              conferenceId:
                props.filter === "conference"
                  ? values.conferenceId
                  : props.filter === "conferenceDomestic"
                    ? values.conferenceDomesticId
                    : null,
              conferenceDomesticId: null,
              profileId: props.profile?.id,
              scientificResearchProfile: mergedList,
              type: props.type,
              acceptedDay: dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD")),
              submissionDay: dateFormat(values?.submissionDay || moment().format("YYYY-MM-DD")),
              accepted: props.type === "working" ? true : false,
              page: typeof values.page === "number" ? values.page.toString() : values.page,
              // type: props.filter === 'chapter' ? 'book' : 'scientificResearch', // working
            })
            .then((response) => {
              if (response.success && newFiles.length !== 0) {
                props
                  .uploadMultiFile(response.data.scientificResearch.id, newFiles, "scientificResearch")
                  .then((responseUpload) => {
                    if (responseUpload.success)
                      props.updateScientificResearch(response.data.scientificResearch.id, {
                        ...response.data.scientificResearch,
                        fileUrl: responseUpload.data.join("||"),
                      });
                  })
                  .catch();
              }
              props.statisticUnaccepted({
                departmentId: 0,
                profileId: props.profile?.id,
                other: props.other,
              });
              if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
            })
            .catch();
          // } else {
          //   // // console.log("safadfsd");
          //   props.pushNoti({
          //     type: "error",
          //     message:
          //       "Không có dữ liệu hội thảo. Bạn vui lòng nhập ở mục tạp chí khác.",
          //   });
          // }
        }
        if (props.filter === "conferenceDomestic") {
          // if (values.conferenceDomesticId) {
          // console.log("insert conference domestic");
          props
            .insertScientificResearch({
              ...values,
              purpose: undefined,
              roleInPaper: undefined,
              dashboardYear: new Date(dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD"))).getFullYear(),
              listAuthor: tempListAuthor,
              journalId: values?.journalId,
              journalDomesticId: values?.journalDomesticId,
              conferenceId:
                props.filter === "conference"
                  ? values.conferenceId
                  : props.filter === "conferenceDomestic"
                    ? values.conferenceDomesticId
                    : null,
              conferenceDomesticId: null,
              profileId: props.profile?.id,
              scientificResearchProfile: mergedList,
              type: props.type,
              acceptedDay: dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD")),
              submissionDay: dateFormat(values?.submissionDay || moment().format("YYYY-MM-DD")),
              accepted: props.type === "working" ? true : false,
              page: typeof values.page === "number" ? values.page.toString() : values.page,
              // type: props.filter === 'chapter' ? 'book' : 'scientificResearch', // working
            })
            .then((response) => {
              if (response.success && newFiles.length !== 0) {
                props
                  .uploadMultiFile(response.data.scientificResearch.id, newFiles, "scientificResearch")
                  .then((responseUpload) => {
                    if (responseUpload.success)
                      props.updateScientificResearch(response.data.scientificResearch.id, {
                        ...response.data.scientificResearch,
                        fileUrl: responseUpload.data.join("||"),
                      });
                  })
                  .catch();
              }
              props.statisticUnaccepted({
                departmentId: 0,
                profileId: props.profile?.id,
                other: props.other,
              });
              if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
            })
            .catch();
          // } else {
          //   props.pushNoti({
          //     type: "warning",
          //     message:
          //       "Không có dữ liệu hội thảo. Bạn vui lòng nhập ở mục tạp chí khác.",
          //   });
          // }
        }
        if (props.filter !== "conferenceDomestic" && props.filter !== "conference") {
          // console.log("insert scientific normal");
          props
            .insertScientificResearch({
              ...values,
              purpose: undefined,
              roleInPaper: undefined,
              dashboardYear: new Date(dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD"))).getFullYear(),
              listAuthor: tempListAuthor,
              journalId: values?.journalId,
              journalDomesticId: values?.journalDomesticId,
              conferenceId:
                props.filter === "conference"
                  ? values.conferenceId
                  : props.filter === "conferenceDomestic"
                    ? values.conferenceDomesticId
                    : null,
              conferenceDomesticId: null,
              profileId: props.profile?.id,
              scientificResearchProfile: mergedList,
              type: props.type,
              acceptedDay: dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD")),
              submissionDay: dateFormat(values?.submissionDay || moment().format("YYYY-MM-DD")),
              accepted: props.type === "working" ? true : false,
              page: typeof values.page === "number" ? values.page.toString() : values.page,
              // type: props.filter === 'chapter' ? 'book' : 'scientificResearch', // working
            })
            .then((response) => {
              if (response.success && newFiles.length !== 0) {
                props
                  .uploadMultiFile(response.data.scientificResearch.id, newFiles, "scientificResearch")
                  .then((responseUpload) => {
                    if (responseUpload.success)
                      props.updateScientificResearch(response.data.scientificResearch.id, {
                        ...response.data.scientificResearch,
                        fileUrl: responseUpload.data.join("||"),
                      });
                  })
                  .catch();
              }
              props.statisticUnaccepted({
                departmentId: 0,
                profileId: props.profile?.id,
                other: props.other,
              });
              if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
            })
            .catch();
        }
      } else {
        // change scientific other
        // console.log("insert scientific other");
        props
          .insertScientificResearch({
            ...values,
            purpose: undefined,
            roleInPaper: undefined,
            score: values.score || 0,
            listAuthor: tempListAuthor,
            scientificResearchOtherProfile: mergedList,
            // publishAt: dateFormat(values?.publishAt || moment().format("YYYY-MM-DD")),
            acceptedDay: dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD")),
            submissionDay: dateFormat(values?.submissionDay || moment().format("YYYY-MM-DD")),
            dashboardYear: new Date(dateFormat(values?.acceptedDay || moment().format("YYYY-MM-DD"))).getFullYear(),
            type: "scientificResearchOther",
            page: typeof values.page === "number" ? values.page.toString() : values.page,
          })
          .then((response) => {
            if (response.success && newFiles.length !== 0) {
              // // console.log(response.data, files.length);
              props
                .uploadMultiFile(response.data.id, newFiles, "scientificResearchOther")
                .then((responseUpload) => {
                  if (responseUpload.success)
                    props.updateScientificResearch(response.data.scientificResearch.id, {
                      ...response.data.scientificResearch,
                      fileUrl: responseUpload.data.join("||"),
                    });
                })
                .catch();
            }
            props.statisticUnaccepted({
              departmentId: 0,
              profileId: props.profile?.id,
              other: props.other,
            });
            if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
          })
          .catch();
      }

      setValues(null);
      setProfileList([]);
      setHelperText(false);
      setNewFiles([]);
    } else {
      setHelperText(true);
    }
  };

  const getTypeAsTitle = (type) => {
    switch (type) {
      case "journalInternational":
        return "trên tạp chí quốc tế";
      case "journalDomestic":
        return "trên tạp chí trong nước";
      case "conference":
        return "trên hội thảo quốc tế";
      case "conferenceDomestic":
        return "trên hội thảo trong nước";
      case "chapter":
        return "là chương sách";
      case "workingPaper":
        return "đang thực hiện";
      case "other":
        return "trên tạp chí khác";
      default:
        return "";
    }
  };

  const contractList = [];
  props?.employmentContractListById.forEach((e) => contractList.push(e.contract));

  const profileListFromProps = [{ id: -1, name: "" }];
  props.profileList.forEach((e) => profileListFromProps.push(e));

  // Filter timeResearchDetailList based on filter type without year filtering
  useEffect(() => {
    if (props.timeResearchInformationList?.length > 0) {
      let miniType = "JI";
      if (props.filter === "journalDomestic") miniType = "JD";
      if (props.filter === "conference") miniType = "CI";
      if (props.filter === "conferenceDomestic") miniType = "CD";

      // Get all timeResearchDetail from all years and filter by type
      let allDetails = [];
      props.timeResearchInformationList.forEach((yearData) => {
        if (yearData.timeResearchDetail) {
          allDetails = [...allDetails, ...yearData.timeResearchDetail];
        }
      });

      // Chapter (Chương sách) is reward-only — no "Loại công trình" needed (it has no
      // research-hour points) — so the legacy picker is empty for chapter. The bonus
      // picker uses its own scientificResearch-chapter bucket.
      const matchTypeForBonus = (e) => {
        if (props.filter === "chapter") return e.type === "scientificResearch-chapter";
        return e.type === "scientificResearch-" + miniType;
      };
      const matchTypeForLoaiCT = (e) => {
        if (props.filter === "chapter") return false; // chapter has no hours/points entry
        return e.type === "scientificResearch-" + miniType;
      };
      const baseFiltered = allDetails.filter((e) => !e.allowedUpdateNum);
      setTimeResearchDetailList(baseFiltered.filter((e) => matchTypeForLoaiCT(e) && e.rank !== 1));
      setTimeResearchDetailListForBonus(baseFiltered.filter((e) => matchTypeForBonus(e) && !!e.bonusCode));

      setValues({
        ...values,
        timeResearchDetailCode: null,
        timeResearchDetailCodeForBonus: null,
      });
    }
  }, [props.timeResearchInformationList, props.filter]);

  // const yearList = [];
  // props.timeResearchInformationList
  //   .sort((a, b) => b.year - a.year)
  //   .forEach((e) => {
  //     yearList.push(e.year);
  //   });

  const propsValues = {
    values: values,
    profileList: profileList,
    setValues: setValues,
    handleInsert: handleInsert,
    handleRemove: handleRemove,
    handleChangeForm: handleChangeForm,
    handleChangeProfileList: handleChangeProfileList,
  };

  const LinhVucBaiBao = [
    { value: "GDDT", label: "Giáo dục đào tạo" },
    { value: "KT", label: "Kinh tế" },
    { value: "QTKD", label: "Quản trị kinh doanh" },
    { value: "QL", label: "Quản lý" },
    { value: "KHAC", label: "Khác" },
  ];

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={inserting}
      onClose={handleClose}
      disableBackdropClick
      PaperProps={{
        style: {
          width: "calc(100vw - 240px)",
          height: "100vh",
          maxWidth: "calc(100vw - 240px)",
          maxHeight: "100vh",
          margin: "0 0 0 240px",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 32px",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h5" style={{ fontWeight: 600, marginRight: 24 }}>
          {`Thêm bài báo ${getTypeAsTitle(props.filter)}`}
        </Typography>
        <div style={{ display: "flex", gap: "12px" }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleClose()}
            style={{
              padding: "8px 24px",
              fontSize: "15px",
              fontWeight: 500,
              borderWidth: 2,
            }}
          >
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              props.filter !== "workingPaper" &&
              (!(values?.numberMainAuthor > 0) ||
                !(values?.numOfMember > 0) ||
                (!!["journalInternational", "journalDomestic", "conference", "conferenceDomestic"].find(
                  (e) => props.filter === e,
                ) &&
                  false) ||
                // "Loại công trình" required EXCEPT for Chương sách (no hours/points).
                (props.filter !== "chapter" && !values?.timeResearchDetailCode) ||
                // "Loại công trình tính thưởng" required only when mục đích = Tính thưởng.
                ((values?.purposeId === 2 || values?.purposeId === 99999999) &&
                  !values?.timeResearchDetailCodeForBonus) ||
                (!values?.doi && newFiles.length === 0))
            }
            onClick={() => handleSubmit()}
            style={{
              padding: "8px 24px",
              fontSize: "15px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(63, 81, 181, 0.3)",
            }}
          >
            Xác nhận
          </Button>
        </div>
      </DialogTitle>
      <DialogContent style={{ flex: 1, overflow: "hidden", display: "flex", padding: 0 }}>
        <div
          style={{
            width: "60%",
            padding: "16px 24px",
            overflowY: "auto",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <Grid container justifyContent="center" spacing={2} style={{ marginBottom: 10 }}>
            {props.filter === "journalInternational" && <JournalInternational {...propsValues} />}
            {props.filter === "journalDomestic" && <JournalDomestic {...propsValues} />}
            {props.filter === "conference" && <Conference {...propsValues} />}
            {props.filter === "conferenceDomestic" && <ConferenceDomestic {...propsValues} />}
            {props.filter === "chapter" && <Chapter {...propsValues} />}
            {props.filter === "workingPaper" && <WorkingPaper {...propsValues} />}
            {props.filter === "other" && <Other {...propsValues} />}

            {/* Commented out: Năm học tính giờ nghiên cứu */}
            {props.filter !== "workingPaper" && (
              <>
                {/* <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={timeResearchYear}
                    SelectProps={{ native: true }}
                    label="Năm học tính giờ nghiên cứu"
                    variant="outlined"
                    onChange={(event) => {
                      setTimeResearchYear(parseInt(event.target.value));
                      handleChangeForm("timeResearchDetailCode", null);
                    }}
                  >
                    {yearList.map((e) => (
                      <option key={`${e} - ${e + 1}`} value={parseInt(e)}>
                        {e} - {e + 1}
                      </option>
                    ))}
                  </TextField>
                </Grid> */}
                {/* Chapter (Chương sách) is reward-only and has no hours/points entry,
                    so the legacy "Loại công trình" picker is hidden. */}
                {props.filter !== "chapter" && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Loại công trình"
                      variant="outlined"
                      value={values?.timeResearchDetailCode || null}
                      onChange={(event) => handleChangeForm("timeResearchDetailCode", event.target.value)}
                      InputLabelProps={{
                        shrink: !(
                          values?.timeResearchDetailCode === undefined || values?.timeResearchDetailCode === null
                        ),
                      }}
                      error={false}
                    >
                      {timeResearchDetailList?.length > 0 ? (
                        timeResearchDetailList?.map((e) => (
                          <MenuItem key={e.code} value={e.code}>
                            {e.description}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={null} disabled>
                          Chưa có dữ liệu
                        </MenuItem>
                      )}
                    </TextField>
                    {!values?.timeResearchDetailCode && (
                      <FormHelperText error>Phải chọn một loại công trình!</FormHelperText>
                    )}
                  </Grid>
                )}
                {/* Loại công trình tính thưởng — shown only when mục đích = Tính thưởng. */}
                {(values?.purposeId === 2 || values?.purposeId === 99999999) && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Loại công trình tính thưởng"
                      variant="outlined"
                      value={values?.timeResearchDetailCodeForBonus || null}
                      onChange={(event) => handleChangeForm("timeResearchDetailCodeForBonus", event.target.value)}
                      InputLabelProps={{
                        shrink: !(
                          values?.timeResearchDetailCodeForBonus === undefined ||
                          values?.timeResearchDetailCodeForBonus === null
                        ),
                      }}
                      error={!values?.timeResearchDetailCodeForBonus}
                      helperText={
                        timeResearchDetailListForBonus?.length === 0
                          ? "Chưa có Loại công trình nào được gán bonus_code trong AdminSetting → Bảng tính giờ NC."
                          : "Bắt buộc. Quyết định mức thưởng cơ sở."
                      }
                    >
                      {timeResearchDetailListForBonus?.length > 0 ? (
                        timeResearchDetailListForBonus.map((e) => (
                          <MenuItem key={e.code} value={e.code}>
                            {e.description}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value={null} disabled>
                          Chưa có dữ liệu
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>
                )}
              </>
            )}
            {/* Bonus checkboxes — only when mục đích = Tính thưởng. */}
            {(values?.purposeId === 2 || values?.purposeId === 99999999) && (
              <Grid item xs={12}>
                <RewardBonusCheckboxGroup
                  value={values?.bonusCodes || []}
                  onChange={(codes) => handleChangeForm("bonusCodes", codes)}
                  disabled={values?.locked === "reward"}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="linhVucBaiBao-label" style={{ padding: "0 8px", backgroundColor: "white" }}>
                  Lĩnh vực bài báo
                </InputLabel>
                <Select
                  native
                  fullWidth
                  labelId="linhVucBaiBao-label"
                  value={values?.linhVucBaiBao || "KHAC"}
                  onChange={(event) => handleChangeForm("linhVucBaiBao", event.target.value)}
                >
                  {LinhVucBaiBao.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {props.filter === "journalInternational" || props.filter === "journalDomestic" ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Thuộc sản phẩm của đề tài"
                    variant="outlined"
                    value={values?.thuocDeTai || ""}
                    multiline
                    rows={3}
                    onChange={(event) => handleChangeForm("thuocDeTai", event.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tóm tắt"
                    variant="outlined"
                    value={values?.tomTat || ""}
                    multiline
                    rows={3}
                    onChange={(event) => handleChangeForm("tomTat", event.target.value)}
                  />
                </Grid>
              </>
            ) : null}
            {props.filter === "conference" || props.filter === "conferenceDomestic" ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tóm tắt"
                    variant="outlined"
                    value={values?.tomTat || ""}
                    multiline
                    rows={3}
                    onChange={(event) => handleChangeForm("tomTat", event.target.value)}
                  />
                </Grid>
                {/* boolean - Bài báo có tranh luận */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values?.coTranhLuan || false}
                        onChange={(event) => handleChangeForm("coTranhLuan", event.target.checked)}
                        color="primary"
                      />
                    }
                    label="Bài báo có tranh luận"
                  />
                </Grid>
                {/* text - Đơn vị tổ chức */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Đơn vị tổ chức"
                    variant="outlined"
                    value={values?.donViToChuc || ""}
                    onChange={(event) => handleChangeForm("donViToChuc", event.target.value)}
                  />
                </Grid>
                {/* text - địa điểm tổ chức */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Địa điểm tổ chức"
                    variant="outlined"
                    value={values?.diaDiemToChuc || ""}
                    onChange={(event) => handleChangeForm("diaDiemToChuc", event.target.value)}
                  />
                </Grid>
              </>
            ) : null}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Ghi chú"
                variant="outlined"
                value={values?.note || ""}
                multiline
                rows={3}
                onChange={(event) => handleChangeForm("note", event.target.value)}
              />
            </Grid>

            {/* ============== BOX 1: Thành viên (giảng viên/CB) ============== */}
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Thành viên</Typography>
            </Grid>
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>

            <Grid item xs={12}>
              {profileList.map((e, index) => {
                // Two flags drive layout: isInternal (true=NEU staff, false=ngoại bộ).
                // Students never appear here — they live in studentList / Box 2.
                const memberType = e.isInternal === false ? "external" : "internal";
                const handleChangeMemberType = (next) => {
                  const updates = {
                    internal: { isInternal: true, isStudent: false, externalName: "" },
                    external: { isInternal: false, isStudent: false, profileId: null, orgCount: 1, orgRatio: 1 },
                  }[next] || {};
                  const merged = { ...profileList[index], ...updates };
                  const tmp = [...profileList];
                  tmp.splice(index, 1, merged);
                  setProfileList(tmp);
                  if (next !== "internal" && profileList[index]?.profileId === values?.profileIdForKpi) {
                    handleChangeForm("profileIdForKpi", null);
                  }
                };
                const hasPurposeButton =
                  (values?.purposeId === 1 || values?.purposeId === 99999999) &&
                  props.filter !== "workingPaper";
                return (
                  <Grid
                    key={`list-${index}`}
                    container
                    spacing={2}
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 6,
                      padding: 12,
                      marginBottom: 12,
                      marginLeft: 0,
                      width: "100%",
                    }}
                  >
                    {/* ----- Row 1 (xs sum = 12): identity ----- */}
                    <Grid item xs={2}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          id={`memberType-label-${index}`}
                          style={{ padding: "0 8px", backgroundColor: "white" }}
                        >
                          Loại thành viên
                        </InputLabel>
                        <Select
                          native
                          fullWidth
                          labelId={`memberType-label-${index}`}
                          value={memberType}
                          onChange={(event) => handleChangeMemberType(event.target.value)}
                        >
                          <option value="internal">Nội bộ</option>
                          <option value="external">Ngoại bộ</option>
                        </Select>
                      </FormControl>
                    </Grid>
                    {memberType === "internal" ? (
                      <>
                        <Grid item xs={4}>
                          <Autocomplete
                            disableClearable
                            options={profileListFromProps}
                            getOptionLabel={(option) => option.name}
                            value={
                              profileListFromProps?.find((profile) => profile.id === e.profileId) ||
                              profileListFromProps[0]
                            }
                            onChange={(event, profile) => {
                              if (profileList[index]?.profileId === values?.profileIdForKpi)
                                handleChangeForm("profileIdForKpi", null);
                              handleChangeProfileList(index, "profileId", parseInt(profile?.id));
                              if (parseInt(profile?.id) >= 0) setHelperText(false);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={helperText && e.profileId === null}
                                size="small"
                                label="Thành viên"
                                variant="outlined"
                              />
                            )}
                          />
                          {helperText && e.profileId === null && (
                            <FormHelperText style={{ color: "red" }}>Không được để trống</FormHelperText>
                          )}
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                            label="Mã viên chức"
                            value={profileListFromProps?.find((profile) => profile.id === e.profileId)?.code || ""}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <TextField
                            fullWidth
                            size="small"
                            disabled
                            variant="outlined"
                            label="Đơn vị"
                            value={
                              profileListFromProps?.find((profile) => profile.id === e.profileId)?.departmentProfile?.[0]
                                ?.department?.name || ""
                            }
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={10}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          label="Tên thành viên ngoại bộ"
                          value={e?.externalName || ""}
                          onChange={(event) => handleChangeProfileList(index, "externalName", event.target.value)}
                          error={helperText && e.externalName === ""}
                          helperText={helperText && e.externalName === "" ? "Không được để trống" : ""}
                        />
                      </Grid>
                    )}

                    {/* ----- Row 2 (xs sum = 12): config + actions ----- */}
                    {memberType === "internal" && (
                      <Grid item xs={2}>
                        <TextField
                          fullWidth
                          size="small"
                          variant="outlined"
                          type="number"
                          label="Số ĐV"
                          // B.7: org_count >=1; org_ratio auto = 1/org_count for the NEU share.
                          inputProps={{ min: 1, step: 1 }}
                          value={e?.orgCount ?? 1}
                          onChange={(event) => {
                            const n = Math.max(1, parseInt(event.target.value, 10) || 1);
                            const tmp = [...profileList];
                            tmp.splice(index, 1, { ...profileList[index], orgCount: n, orgRatio: 1 / n });
                            setProfileList(tmp);
                          }}
                        />
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={
                        memberType === "internal"
                          ? hasPurposeButton
                            ? 3
                            : 5
                          : hasPurposeButton
                            ? 4
                            : 7
                      }
                    >
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          id={`roleInPaper-label-${index}`}
                          style={{ padding: "0 8px", backgroundColor: "white" }}
                        >
                          Vai trò được giao
                        </InputLabel>
                        <Select
                          native
                          fullWidth
                          labelId={`roleInPaper-label-${index}`}
                          value={e?.roleInPaper || "main"}
                          onChange={(event) => handleChangeProfileList(index, "roleInPaper", event.target.value)}
                        >
                          <option value="main">Tác giả chính</option>
                          {props.filter.includes("journal") && (
                            <option value="member">Không phải tác giả chính</option>
                          )}
                          {props.filter.includes("conference") && <option value="member">Đồng tác giả</option>}
                        </Select>
                      </FormControl>
                    </Grid>
                    {hasPurposeButton && values?.purposeId === 1 && (
                      <Grid item xs={3}>
                        <FormControl required fullWidth error={!values?.profileIdForKpi}>
                          <Button
                            fullWidth
                            color="primary"
                            variant="outlined"
                            style={{ height: 40 }}
                            disabled={e.profileId === null}
                            startIcon={
                              typeof values?.profileIdForKpi === "number" && values?.profileIdForKpi === e.profileId ? (
                                <CheckBox />
                              ) : (
                                <CheckBoxOutlineBlank />
                              )
                            }
                            onClick={() => {
                              setDetail(null);
                              props.getAllEmploymentContractByProfileId(parseInt(e.profileId));
                              handleChangeForm(
                                "profileIdForKpi",
                                values?.profileIdForKpi !== e.profileId ? parseInt(e.profileId) : null,
                              );
                            }}
                          >
                            Tính KPI
                          </Button>
                        </FormControl>
                      </Grid>
                    )}
                    {hasPurposeButton && values?.purposeId === 99999999 && (
                      <Grid item xs={3}>
                        <FormControl fullWidth error={!values?.profileIdForReward}>
                          <Button
                            fullWidth
                            color="primary"
                            variant="outlined"
                            style={{ height: 40 }}
                            disabled={e.profileId === null}
                            startIcon={
                              typeof values?.profileIdForReward === "number" &&
                              values?.profileIdForReward === e.profileId ? (
                                <CheckBox />
                              ) : (
                                <CheckBoxOutlineBlank />
                              )
                            }
                            onClick={() => {
                              setDetail(null);
                              handleChangeForm(
                                "profileIdForReward",
                                values?.profileIdForReward !== e.profileId ? parseInt(e.profileId) : null,
                              );
                            }}
                          >
                            Tính thưởng
                          </Button>
                        </FormControl>
                      </Grid>
                    )}
                    <Grid item xs={hasPurposeButton ? 3 : 4}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <InputLabel
                          id={`statusInProject-label-${index}`}
                          style={{ padding: "0 8px", backgroundColor: "white" }}
                        >
                          Trạng thái
                        </InputLabel>
                        <Select
                          native
                          fullWidth
                          labelId={`statusInProject-label-${index}`}
                          value={e?.statusInProject || "K"}
                          onChange={(event) => handleChangeProfileList(index, "statusInProject", event.target.value)}
                        >
                          <option value="NCS">Đang làm NCS</option>
                          <option value="CH">Đang học Cao học</option>
                          <option value="K">Khác</option>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={1}>
                      <Button
                        fullWidth
                        color="secondary"
                        variant="contained"
                        style={{ height: 40 }}
                        onClick={() => handleRemove(index)}
                      >
                        Xóa
                      </Button>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>

            <Grid item xs={12}>
              <Button fullWidth variant="outlined" onClick={handleInsert}>
                Thêm thành viên
              </Button>
            </Grid>

            {/* ============== BOX 2: Sinh viên / Học viên ============== */}
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>
            <Grid item xs={2}>
              <Typography style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.30)" }}>Sinh viên</Typography>
            </Grid>
            <Grid item xs={5} style={{ margin: "auto" }}>
              <Divider />{" "}
            </Grid>

            <Grid item xs={12}>
              {studentList.map((s, index) => (
                <Grid
                  key={`student-${index}`}
                  container
                  spacing={2}
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 6,
                    padding: 12,
                    marginBottom: 12,
                    marginLeft: 0,
                    width: "100%",
                  }}
                >
                  {/* xs sum = 12 */}
                  <Grid item xs={5}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Tên sinh viên / học viên"
                      value={s?.studentName || ""}
                      error={helperText && !s.studentName}
                      helperText={helperText && !s.studentName ? "Không được để trống" : ""}
                      onChange={(event) => handleChangeStudentList(index, "studentName", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Mã sinh viên / học viên"
                      value={s?.studentCode || ""}
                      onChange={(event) => handleChangeStudentList(index, "studentCode", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Mã lớp"
                      value={s?.studentClass || ""}
                      onChange={(event) => handleChangeStudentList(index, "studentClass", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      style={{ height: 40 }}
                      onClick={() => handleRemoveStudent(index)}
                    >
                      Xóa
                    </Button>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button fullWidth variant="outlined" onClick={handleInsertStudent}>
                Thêm sinh viên
              </Button>
            </Grid>
          </Grid>
        </div>
        {/* <div
          style={{
            flexDirection: "column",
            display:
              values?.profileIdForKpi !== null && values?.profileIdForKpi >= 0
                ? "block"
                : "none",
          }}
        >
          <EmploymentContractTable
            contract={detail}
            contractList={contractList}
            kpiIdForKpi={values?.kpiIdForKpi}
            contractIdForKpi={values?.contractIdForKpi}
            setDetail={setDetail}
            values={values}
            setValues={setValues}
            handleChangeForm={handleChangeForm}
          />
          <EmploymentContractDetailTable contract={detail} kpi={detail} />
        </div> */}
        <div
          style={{
            width: "40%",
            padding: "16px 24px",
            overflowY: "auto",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography variant="h6" style={{ marginBottom: 16 }}>
            Upload file minh chứng
          </Typography>
          <div
            style={{
              backgroundColor: "#ffebee",
              color: "#c62828",
              padding: "12px",
              marginBottom: 16,
              borderRadius: 4,
              whiteSpace: "pre-line",
            }}
          >
            {`Upload file Scan bản gốc:
1. Bìa
2. Mục lục có tên tác giả
3. Trang đầu của bài viết
4. Trang có đăng ký số giấy phép xuất bản hoặc số ISSN
5. Đường link. Nếu bài báo không có đường link phải cung cấp đủ các minh chứng trên và toàn bộ bản mềm của bài báo
Ghi chú:
- Việc kê khai công trình phải ghi đầy đủ tên bài báo/bài hội thảo, từ trang bao nhiêu trong tạp chí/kỷ yếu.
- Tác giả chính nếu không có xác nhận của BBT hoặc quy định của Tạp chí thì được hiểu là corresponding author hoặc tác giả đứng đầu.`}
          </div>
          <MultiFileManager
            fileUrls={[]}
            setFileUrls={() => {}}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            disabled={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  fileUrl: select(state, "fileReducer", "fileUrl"),
  profile: select(state, "profileReducer", "detail"),
  profileList: select(state, "profileReducer", "list"),
  employmentContractListById: select(state, "employmentContractReducer", "listById"),
  timeResearchInformationList: select(state, "timeResearchInformationReducer", "list"),
  isFetchingTimeResearchInformation: select(state, "timeResearchInformationReducer", "isFetching"),
});

const mapDispatchToProps = (dispatch) => ({
  pushNoti: (payload) => dispatch(notification(payload)),
  resetDetail: () => dispatch(resetDetail()),
  uploadMultiFile: (id, files, type) => dispatch(uploadMultiFile(id, files, type)),
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  insertScientificResearch: (payload) => dispatch(insertScientificResearch(payload)),
  insertScientificResearchOther: (payload) => dispatch(insertScientificResearchOther(payload)),
  getAllEmploymentContractByProfileId: (id) => dispatch(getAllEmploymentContractByProfileId(id)),
  updateScientificResearch: (id, payload) => dispatch(updateScientificResearch(id, payload)),
  getTimeResearchInformation: (profileId, year) => dispatch(getTimeResearchInformation(profileId, year)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(SolutionInsertModal));

